import {
  type CustomField,
  type CustomFieldObject,
  getObjectFlags,
} from '@clockwise/clients/custom-fields';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { customFieldsApi } from './api';
import { queryKeys } from './query-keys';

const CUSTOM_FIELDS_PREFIX = 'customFields.';

/**
 * Fetches the entire tenant's active custom-field definitions in a single
 * request (`GET /api/custom-fields/by-tenant`) and returns the subset
 * enabled on the requested object kind. One cache key per app session —
 * editor / viewer / filter / setup / audit panels all share it, so the
 * customer detail page (which mounts both the customer viewer and the
 * embedded SOW panel) fires one round-trip instead of two.
 *
 * Gated server-side on `custom_fields.view`. A caller without that
 * permission gets back an empty list and the components render nothing.
 */
export function useCustomFieldDefinitions(object: CustomFieldObject): {
  data: CustomField[] | undefined;
  isLoading: boolean;
} {
  const query = useTenantCustomFieldDefinitions();
  const filtered = useMemo<CustomField[] | undefined>(() => {
    if (query.data == null) return undefined;
    return query.data.filter((d) => getObjectFlags(d, object).enabled);
  }, [query.data, object]);
  return { data: filtered, isLoading: query.isLoading };
}

/**
 * Raw tenant-wide fetch — exposed for the rare consumer that needs every
 * definition (e.g. the resolver hook below, or future debug surfaces).
 * Most components should call `useCustomFieldDefinitions(object)` so the
 * object filter applies consistently.
 */
export function useTenantCustomFieldDefinitions() {
  return useQuery({
    queryKey: queryKeys.customFields.byTenant(),
    queryFn: () => customFieldsApi.byTenant().then((r) => r.data),
    staleTime: 60_000,
  });
}

/**
 * Resolver for `customFields.<uuid>` audit field paths → the definition's
 * human label. Use in the audit panel to swap raw UUIDs for the user-facing
 * field name.
 *
 * Pass the row's existing `label` as the fallback so non-customFields rows
 * keep their domain-declared label (`'Name'`, `'Status'`, …) and only
 * `customFields.<uuid>` paths get translated. The `object` parameter is
 * kept for API symmetry with the old per-object fetch but is no longer
 * used to scope the lookup — the tenant-wide cache covers every object's
 * fields.
 */
export function useResolveCustomFieldLabel(
  _object: CustomFieldObject,
): (fieldPath: string, fallback: string) => string {
  const { data } = useTenantCustomFieldDefinitions();
  return useMemo(() => {
    const labelById = new Map((data ?? []).map((d) => [d.id, d.label]));
    return (fieldPath: string, fallback: string): string => {
      if (!fieldPath.startsWith(CUSTOM_FIELDS_PREFIX)) return fallback;
      const id = fieldPath.slice(CUSTOM_FIELDS_PREFIX.length);
      return labelById.get(id) ?? id;
    };
  }, [data]);
}
